import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated.
  // Session is a HttpOnly cookie set by the backend — we can't peek at it
  // from JS, so we probe /auth/me on mount and rely on the response.
  const [user, setUser] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    try {
      // Response body still carries {token,user} for backward compat, but
      // in the browser we ignore `token` — the backend sets the session +
      // CSRF cookies on this response and axios sends them from now on.
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (name, email, password, legal = {}) => {
    try {
      const { data } = await api.post("/auth/register", {
        name, email, password,
        accepted_terms_version: legal.terms || undefined,
        accepted_privacy_version: legal.privacy || undefined,
      });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    // Fire-and-forget: even if the request fails (network / already logged
    // out), we still want to reset the UI. The backend endpoint always
    // clears cookies without requiring a valid session.
    try {
      await api.post("/auth/logout");
    } catch {
      // ignored
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      role: (user && typeof user === "object" && user.role) || "admin",
      isCaller: !!(user && typeof user === "object" && user.role === "caller"),
      login,
      register,
      logout,
      reload: loadMe,
    }),
    [user, login, register, logout, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
